import Image from "next/image";
import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import { CheckCircle2 } from "lucide-react";
import {
  SOCIAL_PROOF_COPY,
  SOCIAL_PROOF_ITEMS,
} from "./homeContent";
import {
  HomeCard,
  SectionContainer,
  SectionHeading,
  SectionShell,
} from "./homeUi";

export function HomeSocialProof() {
  return (
    <SectionShell alt>
      <SectionContainer>
        <SectionHeading
          eyebrow={SOCIAL_PROOF_COPY.eyebrow}
          title={SOCIAL_PROOF_COPY.title}
          description={SOCIAL_PROOF_COPY.description}
        />

        <Flex justify="center" mb={{ base: 8, md: 10 }} mt={{ base: -4, md: -6 }}>
          <HStack
            spacing={2}
            px={3}
            py={1.5}
            borderRadius="full"
            bg="blackAlpha.400"
            border="1px solid"
            borderColor="whiteAlpha.150"
          >
            <Box color="orange.900" display="flex">
              <CheckCircle2 size={16} aria-hidden />
            </Box>
            <Text fontSize="sm" color="barber.100" fontWeight="medium">
              {SOCIAL_PROOF_COPY.authenticityLabel}
            </Text>
          </HStack>
        </Flex>

        {/* Mobile: horizontal scroll + snap */}
        <Box
          display={{ base: "block", md: "none" }}
          mx={{ base: -4, sm: -6 }}
          px={{ base: 4, sm: 6 }}
          overflowX="auto"
          overflowY="hidden"
          css={{
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Flex gap={4} w="max-content" pb={2}>
            {SOCIAL_PROOF_ITEMS.map((item) => (
              <ProofCard key={item.id} item={item} mobile />
            ))}
          </Flex>
        </Box>

        {/* Tablet + desktop: grid */}
        <Box
          display={{ base: "none", md: "grid" }}
          gridTemplateColumns={{
            md: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ md: 6, lg: 8 }}
        >
          {SOCIAL_PROOF_ITEMS.map((item) => (
            <ProofCard key={item.id} item={item} />
          ))}
        </Box>
      </SectionContainer>
    </SectionShell>
  );
}

type ProofCardProps = {
  item: (typeof SOCIAL_PROOF_ITEMS)[number];
  mobile?: boolean;
};

function ProofCard({ item, mobile }: ProofCardProps) {
  return (
    <HomeCard
      p={{ base: 2, md: 3 }}
      h="full"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      w={mobile ? "86vw" : "auto"}
      maxW={mobile ? "360px" : undefined}
      css={mobile ? { scrollSnapAlign: "center" } : undefined}
      _hover={{
        transform: "translateY(-2px)",
        borderColor: "whiteAlpha.250",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.24)",
      }}
    >
      <Box
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="barber.400"
        borderRadius="xl"
        overflow="hidden"
        sx={{
          "& img": {
            width: "100%",
            height: "auto",
            maxHeight: { base: "68vh", md: "480px", lg: "520px" },
            objectFit: "contain",
            display: "block",
          },
        }}
      >
        <Image
          src={item.src}
          alt={item.alt}
          width={item.width}
          height={item.height}
          sizes="(max-width: 767px) 85vw, (max-width: 991px) 50vw, 33vw"
        />
      </Box>
    </HomeCard>
  );
}
